import React, { Component } from 'react'
import { AutoComplete, Select, Icon, Input, Button } from 'antd';
import { connect } from 'react-redux';
import { getSupplies } from '../redux/actions/supply';
import { viewSupply } from '../redux/actions/viewSupply';

const Option = AutoComplete.Option;
const SelectOption = Select.Option;

function formatPrice(v) {
    return `$${Number(v || 0).toFixed(2)}`;
}

function variantOptionText(v) {
    return v.optionLabel ? `${v.optionLabel} — ${formatPrice(v.price)}` : formatPrice(v.price);
}

// Bill-of-materials picker for a kit: search Supply by name, pick one exact
// variant, set qty needed per kit. Each row only ever persists as
// {supplyId, variantId, qtyPerKit} server-side (see admin/pattern.js's kit
// parsing) — the extra display fields carried here (supplyName, optionLabel,
// variantWeightOz, variantQuantity, supplyDescription) are convenience only
// and get silently dropped on save.
//
// Existing rows loaded from a saved pattern won't have those display fields
// (the DB only stores the three real ones), so this resolves them itself via
// a per-supply GET, cached locally — same trick PatternPicker uses to avoid
// stomping on the shared redux search reducer when multiple pickers render
// at once.
class SupplyIngredientPicker extends Component {
    constructor(props) {
        super(props);
        this.state = {
            query: '',
            results: [],
            pendingSupply: null,
            pendingVariantId: '',
            pendingQty: 1,
            resolved: {},
        };
    }

    componentDidMount() {
        this.resolveMissing();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.value !== this.props.value) {
            this.resolveMissing();
        }
    }

    resolveMissing = () => {
        let { value } = this.props;
        (value || []).forEach(ing => {
            if (!ing.supplyName && ing.supplyId && !this.state.resolved[ing.supplyId]) {
                this.props.dispatch(viewSupply(ing.supplyId)).then(res => {
                    let supply = res && res.data && res.data.data;
                    if (supply) {
                        this.setState(prev => ({ resolved: { ...prev.resolved, [ing.supplyId]: supply } }));
                    }
                }).catch(() => {});
            }
        });
    }

    handleSearch = (q) => {
        this.setState({ query: q, pendingSupply: null, pendingVariantId: '' });
        if (!q || !q.trim()) {
            this.setState({ results: [] });
            return;
        }
        this.props.dispatch(getSupplies({ search: q.trim(), length: 20 })).then(res => {
            let matches = (res && res.data && res.data.data) || [];
            this.setState({ results: matches });
        }).catch(() => {
            this.setState({ results: [] });
        })
    }

    handleSelectSupply = (val, option) => {
        let supply = JSON.parse(option.props.value);
        let variants = supply.variants || [];
        // Only one option to begin with — skip the extra click and pick it now.
        let autoVariantId = variants.length === 1 ? variants[0]._id : '';
        this.setState({ pendingSupply: supply, pendingVariantId: autoVariantId, pendingQty: 1, query: supply.name, results: [] });
    }

    handleAdd = () => {
        let { pendingSupply, pendingVariantId, pendingQty } = this.state;
        if (!pendingSupply || !pendingVariantId) return;
        let variant = (pendingSupply.variants || []).find(v => v._id === pendingVariantId);
        if (!variant) return;
        let { value, onChange } = this.props;
        let current = value || [];
        onChange([...current, {
            supplyId: pendingSupply._id,
            variantId: variant._id,
            qtyPerKit: Number(pendingQty) || 1,
            supplyName: pendingSupply.name,
            supplyDescription: pendingSupply.description,
            optionLabel: variant.optionLabel,
            variantPrice: variant.price,
            variantWeightOz: variant.weightOz,
            variantQuantity: variant.quantity,
        }]);
        this.setState({ pendingSupply: null, pendingVariantId: '', pendingQty: 1, query: '', results: [] });
    }

    handleRemove = (index) => {
        let { value, onChange } = this.props;
        let updated = (value || []).slice();
        updated.splice(index, 1);
        onChange(updated);
    }

    render() {
        let { query, results, pendingSupply, pendingVariantId, pendingQty, resolved } = this.state;
        let ingredients = this.props.value || [];
        let multiVariant = pendingSupply && (pendingSupply.variants || []).length > 1;
        let singleVariant = pendingSupply && (pendingSupply.variants || []).length === 1 ? pendingSupply.variants[0] : null;

        let totalWeight = 0;
        let hasWeight = false;
        let availableQty = null;
        ingredients.forEach(ing => {
            let resolvedSupply = resolved[ing.supplyId];
            let resolvedVariant = resolvedSupply && (resolvedSupply.variants || []).find(v => v._id === ing.variantId);

            let w = ing.variantWeightOz !== undefined && ing.variantWeightOz !== null ? ing.variantWeightOz : (resolvedVariant && resolvedVariant.weightOz);
            if (w !== undefined && w !== null) {
                totalWeight += w * (ing.qtyPerKit || 1);
                hasWeight = true;
            }

            let q = ing.variantQuantity !== undefined && ing.variantQuantity !== null ? ing.variantQuantity : (resolvedVariant && resolvedVariant.quantity);
            if (q !== undefined && q !== null) {
                let possible = Math.floor(q / (ing.qtyPerKit || 1));
                availableQty = availableQty === null ? possible : Math.min(availableQty, possible);
            }
        });

        return (
            <div className="kit-ingredient-picker">
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                    <AutoComplete
                        style={{ width: 260, fontSize: 16 }}
                        value={query}
                        onSearch={this.handleSearch}
                        onSelect={this.handleSelectSupply}
                        onChange={(v) => this.setState({ query: v })}
                        placeholder="Search supplies…"
                    >
                        {results.map(s => (
                            <Option key={s._id} value={JSON.stringify(s)}>{s.name}</Option>
                        ))}
                    </AutoComplete>
                    {multiVariant && (
                        <Select
                            style={{ width: 220, fontSize: 16 }}
                            placeholder="Variant"
                            value={pendingVariantId || undefined}
                            onChange={(v) => this.setState({ pendingVariantId: v })}
                        >
                            {pendingSupply.variants.map(v => (
                                <SelectOption key={v._id} value={v._id}>{variantOptionText(v)}</SelectOption>
                            ))}
                        </Select>
                    )}
                    {singleVariant && (
                        <span style={{ fontSize: 16, color: '#494d4b', whiteSpace: 'nowrap' }}>{variantOptionText(singleVariant)}</span>
                    )}
                    {pendingSupply && (
                        <Input
                            style={{ width: 70, fontSize: 16 }}
                            type="number"
                            min="1"
                            step="1"
                            value={pendingQty}
                            onChange={(e) => this.setState({ pendingQty: e.target.value })}
                            placeholder="Qty"
                        />
                    )}
                    {pendingSupply && pendingVariantId && (
                        <Button type="primary" style={{ fontSize: 16 }} onClick={this.handleAdd}>
                            Add
                        </Button>
                    )}
                </div>
                {ingredients.length > 0 && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16, marginBottom: 16 }}>
                        <tbody>
                            {ingredients.map((ing, i) => {
                                let resolvedSupply = resolved[ing.supplyId];
                                let resolvedVariant = resolvedSupply && (resolvedSupply.variants || []).find(v => v._id === ing.variantId);
                                let displayName = ing.supplyName || (resolvedSupply && resolvedSupply.name) || 'Loading…';
                                let displayVariant = ing.optionLabel || (resolvedVariant && resolvedVariant.optionLabel);
                                let displayPrice = ing.variantPrice !== undefined ? ing.variantPrice : (resolvedVariant && resolvedVariant.price);
                                let displayDesc = ing.supplyDescription !== undefined ? ing.supplyDescription : (resolvedSupply && resolvedSupply.description);
                                let cellStyle = { padding: '18px 16px 18px 0', fontSize: 16, fontWeight: 400, color: '#494d4b', verticalAlign: 'top', borderBottom: '1px solid #eee' };

                                return (
                                    <tr key={i}>
                                        <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>
                                            {displayName}{displayVariant ? ` — ${displayVariant}` : ''}
                                        </td>
                                        <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>
                                            {displayPrice !== undefined && displayPrice !== null ? formatPrice(displayPrice) : ''}
                                        </td>
                                        <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>×{ing.qtyPerKit || 1}</td>
                                        <td style={{ ...cellStyle, color: '#717c81', width: '45%' }}>{displayDesc || ''}</td>
                                        <td style={{ ...cellStyle, textAlign: 'right', width: 32 }}>
                                            <Icon
                                                type="minus-circle-o"
                                                className="dynamic-delete-button"
                                                style={{ cursor: 'pointer', position: 'static' }}
                                                onClick={() => this.handleRemove(i)}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
                {ingredients.length > 0 && (
                    <div style={{ fontSize: 16, color: '#494d4b' }}>
                        Quantity available: {availableQty === null ? '∞' : availableQty}
                        {hasWeight ? ` · ${totalWeight.toFixed(1)} oz` : ''}
                    </div>
                )}
            </div>
        );
    }
}

export default connect()(SupplyIngredientPicker);
